import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useEditor, EditorContent, type JSONContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { useMutation, useAction } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Id } from '../../../../convex/_generated/dataModel'
import { useVideoSeekOptional } from '@/contexts/video-seek-context'
import { ScrollText, RefreshCw, Scissors, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { WordMark } from './extensions/WordMark'
import { SceneBlock } from './extensions/SceneBlock'
import { DialogBlock } from './extensions/DialogBlock'
import { createCharacterMention } from './extensions/CharacterMention'
import { buildDocumentFromClips, type ClipData, type CharacterData, type TtsTimestamp } from './buildDocument'
import { useScriptEditPropagation } from './useScriptEditPropagation'

interface ScriptPanelProps {
  clips: ClipData[]
  characters: CharacterData[]
  projectTtsStatus?: string
  projectTtsTimestamps?: TtsTimestamp[]
  workflowId?: Id<'workflows'>
  onSelectSegment?: (startTime: number, endTime: number, text: string) => void
}

interface WordSelection {
  startTime: number
  endTime: number
  text: string
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

export function ScriptPanel({ clips, characters, projectTtsStatus, projectTtsTimestamps, workflowId, onSelectSegment }: ScriptPanelProps) {
  const videoSeek = useVideoSeekOptional()
  const updateClipDialog = useMutation(api.workflows.updateClipDialog)
  const reorderClipsMutation = useMutation(api.workflows.reorderClips)
  const regenerateTTS = useAction(api.ttsGeneration.regenerateProjectTTS)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [wordSelection, setWordSelection] = useState<WordSelection | null>(null)
  const { pendingEdit, initializeFromDoc, checkForDeletions, confirmTrim, dismiss: dismissEdit } = useScriptEditPropagation(clips, workflowId)
  const saveTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({})
  const editorContainerRef = useRef<HTMLDivElement>(null)
  const isUpdatingFromClips = useRef(false)

  const originalTextByClip = useRef<Record<string, string>>({})

  useEffect(() => {
    for (const clip of clips) {
      if (originalTextByClip.current[clip._id] !== undefined) continue
      if (clip.dialog?.length) {
        originalTextByClip.current[clip._id] = clip.dialog.map((d) => d.text).join('\n')
      } else if (clip.transcription?.text) {
        originalTextByClip.current[clip._id] = clip.transcription.text
      }
    }
  }, [clips])

  const ttsReadyClips = useMemo(() => {
    const set = new Set<string>()
    const hasProjectTts = projectTtsStatus === 'ready' || projectTtsStatus === 'stale'
    for (const clip of clips) {
      if (clip.ttsStatus === 'ready' || hasProjectTts) set.add(clip._id)
    }
    return set
  }, [clips, projectTtsStatus])

  const doc = useMemo(() => {
    const d = buildDocumentFromClips(clips, characters, projectTtsStatus, projectTtsTimestamps)
    initializeFromDoc(d)
    return d
  }, [clips, characters, projectTtsStatus, projectTtsTimestamps, initializeFromDoc])

  const characterMention = useMemo(
    () => createCharacterMention(characters.map((c) => ({ id: c.id, name: c.name }))),
    [characters],
  )

  const handleWordClick = useCallback(
    (start: number) => {
      if (videoSeek) {
        videoSeek.seekTo(start)
      }
    },
    [videoSeek],
  )

  const markStaleAudio = useCallback(
    (_ed: ReturnType<typeof useEditor>, clipId: string, currentText: string) => {
      if (!ttsReadyClips.has(clipId) || !editorContainerRef.current) return
      const original = originalTextByClip.current[clipId] || ''
      const isStale = currentText.trim() !== original.trim()
      const blocks = editorContainerRef.current.querySelectorAll(
        `[data-dialog-block][data-clip-id="${clipId}"]`,
      )
      blocks.forEach((el) => {
        if (isStale) {
          el.setAttribute('data-audio-stale', 'true')
        } else {
          el.removeAttribute('data-audio-stale')
        }
      })
    },
    [ttsReadyClips, originalTextByClip],
  )

  const checkWordSelection = useCallback(() => {
    if (!editorContainerRef.current) return
    const sel = window.getSelection()
    if (!sel || sel.isCollapsed || !sel.rangeCount) {
      setWordSelection(null)
      return
    }
    const range = sel.getRangeAt(0)
    const container = editorContainerRef.current
    if (!container.contains(range.commonAncestorContainer)) {
      setWordSelection(null)
      return
    }
    const wordEls = container.querySelectorAll('[data-word-start]')
    let firstStart = Infinity
    let lastEnd = -Infinity
    const textParts: string[] = []
    for (const el of wordEls) {
      if (sel.containsNode(el, true)) {
        const s = parseFloat(el.getAttribute('data-word-start') || '0')
        const e = parseFloat(el.getAttribute('data-word-end') || '0')
        if (s < firstStart) firstStart = s
        if (e > lastEnd) lastEnd = e
        textParts.push((el.textContent || '').trim())
      }
    }
    if (textParts.length >= 2 && firstStart < Infinity) {
      setWordSelection({ startTime: firstStart, endTime: lastEnd, text: textParts.join(' ') })
    } else {
      setWordSelection(null)
    }
  }, [])

  useEffect(() => {
    const onMouseUp = () => setTimeout(checkWordSelection, 10)
    document.addEventListener('mouseup', onMouseUp)
    return () => document.removeEventListener('mouseup', onMouseUp)
  }, [checkWordSelection])

  const debouncedSave = useCallback(
    (clipId: string, text: string, sourceType: string, dialogIndex: number, mention?: { id: string; label: string } | null) => {
      const key = `${clipId}:${dialogIndex}`
      if (saveTimeoutRef.current[key]) {
        clearTimeout(saveTimeoutRef.current[key])
      }
      saveTimeoutRef.current[key] = setTimeout(async () => {
        if (sourceType === 'dialog' || sourceType === 'empty') {
          const clip = clips.find((c) => c._id === clipId)
          const existingDialog = clip?.dialog || []
          const idx = dialogIndex
          const fallbackLine = existingDialog[idx] || existingDialog[0]
          const charId = mention?.id || fallbackLine?.characterId || clip?.charactersInClip?.[0] || characters[0]?.id || ''
          const charName = mention?.label || fallbackLine?.characterName || characters.find((c) => c.id === charId)?.name || ''

          if (existingDialog.length > 0 && idx < existingDialog.length) {
            const updated = existingDialog.map((line, i) =>
              i === idx ? { ...line, text, characterId: charId, characterName: charName } : line,
            )
            await updateClipDialog({
              clipId: clipId as Id<'workflowClips'>,
              dialog: updated,
            })
          } else if (text.trim()) {
            await updateClipDialog({
              clipId: clipId as Id<'workflowClips'>,
              dialog: [{ characterId: charId, characterName: charName, text }],
            })
          }
        }
      }, 1200)
    },
    [clips, characters, updateClipDialog],
  )

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: false,
          blockquote: false,
          bulletList: false,
          orderedList: false,
          codeBlock: false,
          horizontalRule: false,
          listItem: false,
        }),
        Placeholder.configure({
          placeholder: ({ node }) => {
            if (node.type.name === 'dialogBlock') {
              const sourceType = node.attrs.sourceType
              if (sourceType === 'empty') return 'Type dialog or paste script...'
              return ''
            }
            return ''
          },
        }),
        WordMark,
        SceneBlock,
        DialogBlock,
        characterMention,
      ],
      content: doc,
      editorProps: {
        attributes: {
          class: 'script-editor-content outline-none',
        },
        handleClick: (_view, _pos, event) => {
          const target = event.target as HTMLElement
          const wordEl = target.closest('[data-word-start]')
          if (wordEl) {
            const start = parseFloat(wordEl.getAttribute('data-word-start') || '0')
            handleWordClick(start)
            return true
          }
          // Fallback: clicking in a dialog block seeks to its clip's start time
          const dialogEl = target.closest('[data-dialog-block]')
          if (dialogEl && videoSeek) {
            const clipId = dialogEl.getAttribute('data-clip-id')
            if (clipId) {
              let cumTime = 0
              for (const c of clips) {
                if (c._id === clipId) { videoSeek.seekTo(cumTime); break }
                cumTime += c.duration || 5
              }
            }
          }
          return false
        },
        handleKeyDown: (_view, event) => {
          if (event.key === 'Escape') {
            ;(_view.dom as HTMLElement).blur()
            return true
          }
          return false
        },
      },
      onUpdate: ({ editor: ed }) => {
        if (isUpdatingFromClips.current) return
        const json = ed.getJSON()
        if (!json.content) return

        checkForDeletions(ed)

        let currentClipId = ''
        for (const node of json.content) {
          if (node.type === 'sceneBlock') {
            currentClipId = node.attrs?.clipId || ''
          }
          if (node.type === 'dialogBlock') {
            const clipId = node.attrs?.clipId || currentClipId
            const sourceType = node.attrs?.sourceType || 'dialog'
            const dialogIndex = node.attrs?.dialogIndex ?? 0
            const text = extractTextFromContent(node.content)
            const mention = extractFirstMention(node.content)
            if (clipId) {
              debouncedSave(clipId, text, sourceType, dialogIndex, mention)
              markStaleAudio(ed, clipId, text)
            }
          }
        }
      },
    },
    [],
  )

  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    isUpdatingFromClips.current = true
    const { from, to } = editor.state.selection
    editor.commands.setContent(doc, { emitUpdate: false })
    try {
      const docSize = editor.state.doc.content.size
      const safeFrom = Math.min(from, docSize)
      const safeTo = Math.min(to, docSize)
      editor.commands.setTextSelection({ from: safeFrom, to: safeTo })
    } catch {
      // selection restore failed, that's ok
    }
    isUpdatingFromClips.current = false
  }, [doc, editor])

  // Word highlighting — use rAF loop during playback for smooth 60fps tracking,
  // fall back to state-driven updates when paused/scrubbing
  const activeWordRef = useRef<Element | null>(null)

  const highlightWord = useCallback((time: number) => {
    const container = editorContainerRef.current
    if (!container) return

    const wordEls = container.querySelectorAll('[data-word-start]')
    let matchEl: Element | null = null
    let matchDist = Infinity

    for (const el of wordEls) {
      const s = parseFloat(el.getAttribute('data-word-start') || '0')
      const e = parseFloat(el.getAttribute('data-word-end') || '0')
      if (time >= s && time <= e) {
        matchEl = el
        matchDist = 0
        break
      }
      const d = Math.min(Math.abs(time - s), Math.abs(time - e))
      if (d < matchDist) {
        matchDist = d
        matchEl = el
      }
    }

    const target = matchDist < 0.5 ? matchEl : null

    // No change — skip DOM work
    if (target === activeWordRef.current) return

    activeWordRef.current?.classList.remove('word-active')
    activeWordRef.current = null

    if (target) {
      target.classList.add('word-active')
      activeWordRef.current = target
      const elRect = (target as HTMLElement).getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      if (elRect.bottom > containerRect.bottom || elRect.top < containerRect.top) {
        (target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [])

  // rAF loop: reads unthrottled currentTimeRef for smooth tracking during playback
  useEffect(() => {
    if (!videoSeek?.isPlaying) return
    let raf: number
    const tick = () => {
      highlightWord(videoSeek.currentTimeRef.current)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [videoSeek?.isPlaying, highlightWord])

  // State-driven fallback: updates when paused (seek, scrub, stop)
  useEffect(() => {
    if (!videoSeek || videoSeek.isPlaying) return
    highlightWord(videoSeek.currentTime)
  }, [videoSeek?.currentTime, videoSeek?.isPlaying, highlightWord])

  const handleCreateClip = useCallback(() => {
    if (!wordSelection || !onSelectSegment) return
    onSelectSegment(wordSelection.startTime, wordSelection.endTime, wordSelection.text)
    setWordSelection(null)
    window.getSelection()?.removeAllRanges()
  }, [wordSelection, onSelectSegment])

  const handleSceneAction = useCallback(async (action: string, _clipId: string) => {
    if (!workflowId || isRegenerating) return
    if (action === 're-narrate' || action === 'regen-clip') {
      setIsRegenerating(true)
      try {
        await regenerateTTS({ workflowId })
      } catch (e) {
        console.error('Failed to regenerate TTS:', e)
      } finally {
        setIsRegenerating(false)
      }
    }
  }, [workflowId, isRegenerating, regenerateTTS])

  useEffect(() => {
    const container = editorContainerRef.current
    if (!container) return
    const handler = (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest('.scene-block-action') as HTMLElement | null
      if (!btn) return
      e.preventDefault()
      e.stopPropagation()
      const action = btn.getAttribute('data-action')
      const clipId = btn.getAttribute('data-clip-id')
      if (action && clipId) handleSceneAction(action, clipId)
    }
    container.addEventListener('click', handler, true)
    return () => container.removeEventListener('click', handler, true)
  }, [handleSceneAction])

  const [dragClipId, setDragClipId] = useState<string | null>(null)

  useEffect(() => {
    const container = editorContainerRef.current
    if (!container) return

    const onDragStart = (e: DragEvent) => {
      const block = (e.target as HTMLElement).closest('[data-scene-block]') as HTMLElement | null
      if (!block) return
      const clipId = block.getAttribute('data-clip-id')
      if (!clipId) return
      setDragClipId(clipId)
      e.dataTransfer?.setData('text/plain', clipId)
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
      block.style.opacity = '0.4'
    }

    const onDragOver = (e: DragEvent) => {
      if (!dragClipId) return
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
      const blocks = container.querySelectorAll('[data-scene-block]')
      let closest: Element | null = null
      let closestDist = Infinity
      for (const block of blocks) {
        const rect = block.getBoundingClientRect()
        const mid = rect.top + rect.height / 2
        const dist = Math.abs(e.clientY - mid)
        if (dist < closestDist) {
          closestDist = dist
          closest = block
        }
      }
      if (closest) {
        const idx = parseInt(closest.getAttribute('clipindex') || closest.getAttribute('data-clip-index') || '0')
        const rect = closest.getBoundingClientRect()
        const insertAfter = e.clientY > rect.top + rect.height / 2
        void (insertAfter ? idx + 1 : idx)
      }
    }

    const onDragEnd = (e: DragEvent) => {
      const block = (e.target as HTMLElement).closest('[data-scene-block]') as HTMLElement | null
      if (block) block.style.opacity = ''
      setDragClipId(null)
    }

    const onDrop = async (e: DragEvent) => {
      e.preventDefault()
      const droppedClipId = e.dataTransfer?.getData('text/plain')
      if (!droppedClipId || !workflowId) return

      const blocks = container.querySelectorAll('[data-scene-block]')
      const currentOrder = Array.from(blocks).map(b => b.getAttribute('data-clip-id') || '')
      const fromIdx = currentOrder.indexOf(droppedClipId)
      if (fromIdx === -1) return

      let closest: Element | null = null
      let closestDist = Infinity
      for (const block of blocks) {
        const rect = block.getBoundingClientRect()
        const mid = rect.top + rect.height / 2
        const dist = Math.abs(e.clientY - mid)
        if (dist < closestDist) { closestDist = dist; closest = block }
      }
      if (!closest) return
      const closestId = closest.getAttribute('data-clip-id') || ''
      const toIdx = currentOrder.indexOf(closestId)
      const rect = closest.getBoundingClientRect()
      const insertAfter = e.clientY > rect.top + rect.height / 2
      let targetIdx = insertAfter ? toIdx + 1 : toIdx

      if (targetIdx > fromIdx) targetIdx--
      if (targetIdx === fromIdx) return

      const newOrder = [...currentOrder]
      newOrder.splice(fromIdx, 1)
      newOrder.splice(targetIdx, 0, droppedClipId)

      const clipIds = newOrder.map(id => clips.find(c => c._id === id)?._id).filter(Boolean) as string[]
      if (clipIds.length === clips.length) {
        await reorderClipsMutation({ workflowId, clipOrder: clipIds as any })
      }

      setDragClipId(null)
      const draggedBlock = container.querySelector(`[data-scene-block][data-clip-id="${droppedClipId}"]`) as HTMLElement | null
      if (draggedBlock) draggedBlock.style.opacity = ''
    }

    container.addEventListener('dragstart', onDragStart)
    container.addEventListener('dragover', onDragOver)
    container.addEventListener('dragend', onDragEnd)
    container.addEventListener('drop', onDrop)
    return () => {
      container.removeEventListener('dragstart', onDragStart)
      container.removeEventListener('dragover', onDragOver)
      container.removeEventListener('dragend', onDragEnd)
      container.removeEventListener('drop', onDrop)
    }
  }, [dragClipId, clips, workflowId, reorderClipsMutation])

  const handleRegenerate = useCallback(async () => {
    if (!workflowId || isRegenerating) return
    setIsRegenerating(true)
    try {
      await regenerateTTS({ workflowId })
    } catch (e) {
      console.error('Failed to regenerate TTS:', e)
    } finally {
      setIsRegenerating(false)
    }
  }, [workflowId, isRegenerating, regenerateTTS])

  if (!clips.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4 py-8">
        <ScrollText size={24} className="text-muted-foreground/50 mb-2" />
        <p className="text-[11px] text-muted-foreground">
          No clips yet. Add clips to see the script editor.
        </p>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden relative">
      {projectTtsStatus === 'stale' && (
        <div className="px-3 py-2 bg-warning/10 border-b border-warning/30 flex items-center justify-between shrink-0">
          <span className="text-[11px] text-warning">Narration is out of sync</span>
          <button
            onClick={handleRegenerate}
            disabled={isRegenerating}
            className="text-[11px] font-medium text-warning hover:text-warning/80 flex items-center gap-1 disabled:opacity-50"
          >
            <RefreshCw size={10} className={isRegenerating ? 'animate-spin' : ''} />
            {isRegenerating ? 'Regenerating...' : 'Regenerate'}
          </button>
        </div>
      )}
      <div
        ref={editorContainerRef}
        className="flex-1 overflow-y-auto script-editor-container"
      >
        <EditorContent editor={editor} className="h-full" />
      </div>

      {wordSelection && onSelectSegment && !pendingEdit && (
        <div className="absolute bottom-4 left-2 right-2 flex items-center justify-center gap-2 px-3 py-2 rounded-xl shadow-lg bg-card border border-border animate-in fade-in slide-in-from-bottom-2 z-10">
          <span className="text-xs text-muted-foreground font-mono shrink-0">
            {formatTime(wordSelection.startTime)}-{formatTime(wordSelection.endTime)}
          </span>
          <Button size="sm" className="gap-1.5 h-7 text-xs shrink-0" onClick={handleCreateClip}>
            <Scissors size={12} />
            Create clip
          </Button>
          <button
            onClick={() => { setWordSelection(null); window.getSelection()?.removeAllRanges() }}
            className="p-1 hover:bg-muted rounded-md text-muted-foreground hover:text-secondary-foreground transition-colors shrink-0"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {pendingEdit && (
        <div className="absolute bottom-4 left-2 right-2 flex items-center justify-center gap-2 px-3 py-2 rounded-xl shadow-lg bg-card border border-border animate-in fade-in slide-in-from-bottom-2 z-10">
          <span className="text-xs text-muted-foreground font-mono shrink-0">
            {formatTime(pendingEdit.removeStart)}-{formatTime(pendingEdit.removeEnd)}
          </span>
          <span className="text-xs text-secondary-foreground max-w-[80px] truncate">
            {pendingEdit.removedText}
          </span>
          {pendingEdit.generationMode === 'uploaded' ? (
            <>
              <Button size="sm" className="gap-1 h-7 text-xs shrink-0" onClick={confirmTrim}>
                <Scissors size={12} />
                Trim
              </Button>
              <Button size="sm" variant="outline" className="gap-1 h-7 text-xs shrink-0" onClick={() => { dismissEdit(); handleRegenerate() }}>
                <RefreshCw size={12} />
                Regen
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" className="gap-1 h-7 text-xs shrink-0" onClick={() => { dismissEdit(); handleRegenerate() }}>
                <RefreshCw size={12} />
                Regen
              </Button>
              <Button size="sm" variant="outline" className="gap-1 h-7 text-xs shrink-0" onClick={confirmTrim}>
                <Scissors size={12} />
                Trim
              </Button>
            </>
          )}
          <button
            onClick={dismissEdit}
            className="p-1 hover:bg-muted rounded-md text-muted-foreground hover:text-secondary-foreground transition-colors shrink-0"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <style>{`
        .script-editor-content {
          padding: 8px 10px;
          font-size: 13px;
          line-height: 1.4;
          color: var(--foreground, #1a1a1a);
        }

        .script-editor-content .scene-block {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 5px 0 2px;
          margin-top: 8px;
          user-select: none;
          cursor: grab;
        }

        .script-editor-content .scene-block:active {
          cursor: grabbing;
        }

        .script-editor-content .scene-block-handle {
          font-size: 11px;
          line-height: 1;
          color: rgba(0,0,0,0.1);
          cursor: grab;
          transition: color 0.15s;
        }

        .script-editor-content .scene-block:hover .scene-block-handle {
          color: rgba(0,0,0,0.25);
        }

        .script-editor-content .scene-block:first-child {
          margin-top: 0;
          padding-top: 2px;
        }

        .script-editor-content .scene-block-label {
          font-size: 9px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: rgba(0,0,0,0.3);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 180px;
        }

        .script-editor-content .scene-block-duration {
          font-size: 9px;
          color: rgba(0,0,0,0.2);
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }

        .script-editor-content .scene-block-action {
          font-size: 9px;
          font-weight: 600;
          color: rgba(245, 158, 11, 0.8);
          background: rgba(245, 158, 11, 0.08);
          border: 1px solid rgba(245, 158, 11, 0.2);
          border-radius: 3px;
          padding: 1px 6px;
          cursor: pointer;
          pointer-events: auto;
          white-space: nowrap;
          transition: all 0.15s;
        }

        .script-editor-content .scene-block-action:hover {
          background: rgba(245, 158, 11, 0.15);
          border-color: rgba(245, 158, 11, 0.4);
          color: rgba(180, 110, 0, 1);
        }

        .script-editor-content .scene-block[data-is-stale="true"] .scene-block-label {
          color: rgba(245, 158, 11, 0.6);
        }

        .script-editor-content .scene-block-line {
          flex: 1;
          height: 1px;
          background: rgba(0,0,0,0.04);
        }

        .script-editor-content .scene-block[data-audio-status="ready"] .scene-block-label::after {
          content: '\\266A';
          margin-left: 4px;
          font-size: 9px;
          color: rgba(34, 197, 94, 0.5);
        }

        .script-editor-content .scene-block[data-audio-status="generating"] .scene-block-label::after {
          content: '\\266A';
          margin-left: 4px;
          font-size: 9px;
          color: rgba(245, 158, 11, 0.5);
          animation: pulse 1.5s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }

        .script-editor-content .dialog-block {
          position: relative;
          padding: 3px 0 3px 12px;
          border-left: 2px solid transparent;
          margin: 1px 0;
          transition: border-color 0.15s;
          min-height: 1.5em;
        }

        .script-editor-content .dialog-block[data-character-name]:not([data-character-name=""])::before {
          content: attr(data-character-name);
          display: block;
          font-size: 9px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: rgba(99, 102, 241, 0.4);
          margin-bottom: 1px;
          pointer-events: none;
        }

        .script-editor-content .dialog-block[data-source-type="transcription"][data-character-name]:not([data-character-name=""])::before {
          color: rgba(34, 197, 94, 0.5);
        }

        .script-editor-content .dialog-block:focus-within,
        .script-editor-content .dialog-block:hover {
          border-left-color: rgba(99, 102, 241, 0.2);
          background: rgba(99, 102, 241, 0.015);
        }

        .script-editor-content .dialog-block[data-source-type="transcription"] {
          border-left-color: rgba(34, 197, 94, 0.1);
        }

        .script-editor-content .dialog-block[data-source-type="transcription"]:hover {
          border-left-color: rgba(34, 197, 94, 0.25);
          background: rgba(34, 197, 94, 0.015);
        }

        .script-editor-content .dialog-block[data-source-type="empty"] {
          border-left-color: rgba(0,0,0,0.04);
        }

        .script-editor-content .dialog-block[data-audio-stale="true"] {
          border-left-color: rgba(245, 158, 11, 0.5);
          background: rgba(245, 158, 11, 0.03);
        }

        .script-editor-content .dialog-block[data-audio-stale="true"]::after {
          content: 'audio stale';
          position: absolute;
          right: 4px;
          top: 3px;
          font-size: 8px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: rgba(245, 158, 11, 0.6);
          pointer-events: none;
        }

        .script-editor-content .word-timed {
          cursor: pointer;
          border-radius: 2px;
          transition: background-color 0.08s, color 0.08s;
        }

        .script-editor-content .word-timed:hover {
          background-color: rgba(59, 130, 246, 0.1);
        }

        .script-editor-content .word-active {
          background-color: rgba(59, 130, 246, 0.45) !important;
          color: rgb(29, 78, 216) !important;
          border-radius: 2px;
          box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.2);
        }

        .script-editor-content .is-empty::before {
          content: attr(data-placeholder);
          color: rgba(0,0,0,0.2);
          pointer-events: none;
          float: left;
          height: 0;
        }

        .script-editor-content p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: rgba(0,0,0,0.2);
          pointer-events: none;
          float: left;
          height: 0;
        }

        .script-editor-content .character-mention {
          background: rgba(99, 102, 241, 0.1);
          color: rgb(99, 102, 241);
          border-radius: 3px;
          padding: 0 3px;
          font-size: 12px;
          font-weight: 600;
          white-space: nowrap;
        }

        .mention-suggestion-popup {
          position: fixed;
          z-index: 9999;
          background: #1a1a1a;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 6px;
          padding: 4px;
          min-width: 140px;
          max-height: 200px;
          overflow-y: auto;
          box-shadow: 0 4px 16px rgba(0,0,0,0.4);
        }

        .mention-suggestion-item {
          padding: 5px 8px;
          border-radius: 4px;
          font-size: 12px;
          color: rgba(255,255,255,0.8);
          cursor: pointer;
          transition: background 0.1s;
        }

        .mention-suggestion-item:hover,
        .mention-suggestion-item.is-selected {
          background: rgba(99, 102, 241, 0.2);
          color: white;
        }
      `}</style>
    </div>
  )
}

function extractTextFromContent(content?: JSONContent[]): string {
  if (!content) return ''
  return content
    .map((node) => {
      if (node.type === 'text') return node.text || ''
      if (node.type === 'mention') return `@${node.attrs?.label || node.attrs?.id || ''}`
      if (node.content) return extractTextFromContent(node.content)
      return ''
    })
    .join('')
}

function extractFirstMention(content?: JSONContent[]): { id: string; label: string } | null {
  if (!content) return null
  for (const node of content) {
    if (node.type === 'mention' && node.attrs?.id) {
      return { id: node.attrs.id, label: node.attrs.label || node.attrs.id }
    }
    if (node.content) {
      const found = extractFirstMention(node.content)
      if (found) return found
    }
  }
  return null
}
